import {
  useRef,
  useState,
  useEffect,
  Fragment,
} from 'react';
import type {
  ChangeEvent,
} from 'react';
import {
  useAlerts,
  useWishlists,
  useAuthentication,
} from '@dinosave/core/stores';
import type {
  WishlistEntity,
} from '@dinosave/core/types';
import {
  Modal,
} from '@/components/Modals';
import type {
  ModalProps,
} from '@/components/Modals';
import EmojiModal from '@/components/Modals/EmojiModal';
import WishlistDelete from '@/components/Wishlists/WishlistDelete';
import WishlistPrivacy from '@/components/Wishlists/WishlistPrivacy';
import {
  AUTH,
} from '@/constants/namespaces';
import useModal from '@/hooks/useModal';
import Button from '@/ui/components/Button';
import Input from '@/ui/components/Input';
import Text from '@/ui/components/Text';
import {
  ShoppingBagIcon,
} from '@/ui/icons';

import styles from './WishlistModal.module.css';

type WishlistModalProps = ModalProps & {
  wishlist?: WishlistEntity;
}

const WishlistModal = (props: WishlistModalProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    isOpen,
    wishlist,
    onClose,
    ...rest
  } = props;

  const wishlistId = wishlist?.id;
  const wishlistName = wishlist?.name;
  const wishlistEmoji = wishlist?.emoji;
  const wishlistPrivacy = wishlist?.privacy || 'friends';

  // ##########################################################################
  // STORES
  // ##########################################################################

  const authenticatedUser = useAuthentication(s => s.authenticatedUser);
  const authenticatedUserSlug = authenticatedUser?.slug;

  const createUserWishlist = useWishlists(AUTH, s => s.createUserWishlist);
  const updateUserWishlist = useWishlists(AUTH, s => s.updateUserWishlist);

  const addSuccess = useAlerts(s => s.addSuccess);

  // ##########################################################################
  // STATE
  // ##########################################################################

  const emojiRef = useRef(null);
  const emojiPortal = emojiRef.current;

  const [name, setName] = useState(wishlistName || '');
  const [nameInvalid, setNameInvalid] = useState(false);
  const [emoji, setEmoji] = useState(wishlistEmoji || null);
  const [privacy, setPrivacy] = useState(wishlistPrivacy);
  const [loading, setLoading] = useState(false);

  const emojiModal = useModal();

  // ##########################################################################
  // CONSTANTS
  // ##########################################################################

  const title = wishlist ? 'Edit wishlist' : 'Add wishlist';
  const confirmText = wishlistId ? 'Save' : 'Create';

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleClose = () => {
    if (
      emojiModal.isOpen
    ) return emojiModal.close();

    if (
      loading
    ) return;

    onClose();
  };

  const handleUpdate = async (wishlist: WishlistEntity) => {
    if (!authenticatedUser) return;

    const updatedWishlist = await updateUserWishlist(authenticatedUserSlug, wishlist);

    addSuccess({
      message: 'Changes saved',
    });

    return updatedWishlist;
  };

  const handleCreate = async (wishlist: WishlistEntity) => {
    if (
      !authenticatedUser
    ) return;

    const createdWishlist = await createUserWishlist(authenticatedUserSlug, wishlist);

    addSuccess({
      title: 'Created',
      message: name,
    });

    return createdWishlist;
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;

    if (
      name
    ) setNameInvalid(false);

    setName(name);
  };

  const handleEmojiChange = (emoji: string | null) => {
    setEmoji(emoji);
    emojiModal.close();
  };

  const handleSubmit = async () => {
    if (
      loading
    ) return;

    const wishlistName = name.trim();

    if (
      !wishlistName
    ) return setNameInvalid(true);

    const wishlistData = {
      ...wishlist,
      emoji,
      privacy,
      name: wishlistName,
    };

    try {
      setLoading(true);

      if (wishlist) {
        await handleUpdate(wishlistData);
      } else {
        await handleCreate(wishlistData);
      }

      onClose();
    } finally {
      setLoading(false);
    }
  };

  // ##########################################################################
  // EFFECTS
  // ##########################################################################

  useEffect(() => {
    if (
      !isOpen
    ) return;

    setName(wishlistName || '');
    setNameInvalid(false);
    setEmoji(wishlistEmoji || null);
    setPrivacy(wishlistPrivacy);
    emojiModal.close();
  }, [isOpen]);

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <Fragment>
      <Modal
        {...rest}
        isOpen={isOpen}
        title={title}
        confirmText={confirmText}
        onClose={handleClose}
        onCancel={handleClose}
        onConfirm={handleSubmit}
        className={styles.wishlistModal}
        contentClassName={styles.content}
      >
        <EmojiModal
          {...emojiModal}
          portal={emojiPortal}
          className={styles.emojiModal}
          onChange={handleEmojiChange}
          dropdown
        />

        <div className={styles.name}>
          <Text kind="label">
            NAME
          </Text>

          <div className={styles.row}>
            <Input
              value={name}
              onChange={handleNameChange}
              placeholder="Sneakers"
              invalid={nameInvalid}
              autoFocus={!wishlist}
              autoComplete="off"
            />

            <div
              ref={emojiRef}
              className={styles.emoji}
            >
              <Button
                color="secondary"
                onClick={emojiModal.toggle}
                centered
              >
                {emoji ? (
                  <Text
                    kind="paragraph"
                    size="regular"
                  >
                    {emoji}
                  </Text>
                ) : (
                  <ShoppingBagIcon />
                )}
              </Button>
            </div>
          </div>
        </div>

        <div className={styles.privacy}>
          <Text kind="label">
            PRIVACY
          </Text>

          <WishlistPrivacy
            value={privacy}
            onChange={setPrivacy}
          />
        </div>

        {wishlist && (
          <div className={styles.delete}>
            <Text kind="label">
              DELETE
            </Text>

            <WishlistDelete
              wishlist={wishlist}
              onSuccess={onClose}
            />
          </div>
        )}
      </Modal>
    </Fragment>
  );
};

export default WishlistModal;
